import {useMutation} from "@apollo/client";
import isEmpty from "lodash/isEmpty";
import {useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {dynamicTabConfig, TabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {TabForm} from "@/components/TabForm/TabForm.tsx";
import {useSubmitWrapper} from "@/lib/hooks/useSubmitWrapper.ts";
import {useTabs} from "@/lib/hooks/useTabs.tsx";
import {
  CreateMaintenanceScheduleResult,
  EditMaintenanceScheduleResult,
  GQL_MAINTENANCE_SCHEDULE_CREATE,
  GQL_MAINTENANCE_SCHEDULE_EDIT,
  GQL_MAINTENANCE_SCHEDULE_GET,
  GQL_MAINTENANCE_SERVICES_FILTER_LIST,
} from "@/lib/queries/maintenance.ts";
import {ResponseStatus, ServerErrors} from "@/lib/types/common.ts";
import {MaintenanceScheduleEditData} from "@/lib/types/maintenance.ts";
import {
  MaintenanceScheduleFormData,
  MaintenanceScheduleTabs,
} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.types.ts";
import {
  defaultMaintenanceScheduleConfig,
  getStaticMaintenanceScheduleTabConfig,
  MaintenanceScheduleFieldList,
  MaintenanceScheduleFieldToTabMap,
  maintenanceScheduleFormDataProcessing,
  MaintenanceScheduleFormDefaultValues,
  MaintenanceScheduleFormPrevalidationFields,
  MaintenanceScheduleYupResolver,
  scheduleToFormData,
} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.utils.tsx";
import {MaintenanceDashboardTabs} from "@/pages/maintenance/maintenance-dashboard/MaintenanceDashboard.types.ts";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

import styles from "./CreateEditMaintenanceScheduleForm.module.scss";

interface CreateEditMaintenanceScheduleFormProps {
  schedule?: MaintenanceScheduleEditData;
}

export const CreateEditMaintenanceScheduleForm = ({schedule}: CreateEditMaintenanceScheduleFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;

  const {tab, changeTab: setTab} = useTabs({
    defaultTab: MaintenanceScheduleTabs.GENERAL,
    tabList: Object.values(MaintenanceScheduleTabs),
    // ?duplicate=true is set by the Duplicate table action and read by
    // EditMaintenanceScheduleContent to strip the schedule id; if it is lost on
    // a tab change the form reverts to editing (and overwriting) the original.
    preserveSearchParams: ["duplicate"],
  });

  const [errorsNotInForm, setErrorsNotInForm] = useState<string[] | undefined>(undefined);
  const tabState: Record<MaintenanceScheduleTabs, dynamicTabConfig> = defaultMaintenanceScheduleConfig;

  const isEditMode = schedule && schedule.id;
  const isDuplicate = Boolean(schedule && !schedule.id);

  const [createMaintenanceSchedule, {data: createData}] = useMutation<CreateMaintenanceScheduleResult>(
    GQL_MAINTENANCE_SCHEDULE_CREATE
  );

  const [editMaintenanceSchedule, {data: editData}] =
    useMutation<EditMaintenanceScheduleResult>(GQL_MAINTENANCE_SCHEDULE_EDIT);

  const defaultValues = useMemo(
    () =>
      schedule
        ? scheduleToFormData(schedule)
        : {
            ...MaintenanceScheduleFormDefaultValues,
            start_date: new Date(),
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const methods = useForm<MaintenanceScheduleFormData>({
    defaultValues,
    reValidateMode: "onChange",
    mode: "onChange",
    resolver: MaintenanceScheduleYupResolver,
  });

  // A duplicate mounts pre-filled via defaultValues (MultiServiceSelect only
  // reads form values at mount), which leaves the form pristine. Re-baseline
  // dirty tracking against the blank defaults so the copied values count as
  // unsaved changes: this enables Create and arms the navigation blocker
  // through the regular isDirty path.
  useEffect(() => {
    if (isDuplicate) {
      const values = methods.getValues();
      methods.reset(MaintenanceScheduleFormDefaultValues, {keepValues: true});
      methods.reset(values, {keepDefaultValues: true});
    }
  }, [isDuplicate, methods]);

  const goBack = useCallback(() => {
    const previousPath = location.state?.from?.pathname;
    const defaultPath = "/maintenance?tab=schedules";
    navigate(previousPath || defaultPath);
  }, [location.state?.from?.pathname, navigate]);

  const data = isEditMode ? editData : createData;
  const maintenanceScheduleErrors =
    data?.response?.status === ResponseStatus.ERROR ? (data?.response?.message as ServerErrors) : undefined;

  const [serverErrorsState, setServerErrorsState] = useState<ServerErrors | undefined>(maintenanceScheduleErrors);

  useEffect(() => {
    if (serverErrorsState) {
      const nonFormErrors = setServerErrors(serverErrorsState, methods.setError, "", MaintenanceScheduleFieldList);
      setErrorsNotInForm(nonFormErrors);
    }
  }, [serverErrorsState, methods.setError]);

  const onSubmit = async (formData: MaintenanceScheduleFormData) => {
    let response;

    if (isEditMode && schedule) {
      response = await editMaintenanceSchedule({
        variables: {
          id: schedule.id,
          data: maintenanceScheduleFormDataProcessing(formData, timezone),
        },
        refetchQueries: [
          GQL_MAINTENANCE_SERVICES_FILTER_LIST,
          {query: GQL_MAINTENANCE_SCHEDULE_GET, variables: {id: schedule.id}},
        ],
      });
    } else {
      response = await createMaintenanceSchedule({
        variables: {
          data: maintenanceScheduleFormDataProcessing(formData, timezone),
        },
        refetchQueries: [GQL_MAINTENANCE_SERVICES_FILTER_LIST],
      });
    }

    if (response?.data?.response?.status !== ResponseStatus.SUCCESS) {
      const errors =
        response?.data?.response?.status === ResponseStatus.ERROR
          ? (response?.data?.response?.message as ServerErrors)
          : undefined;
      setServerErrorsState(errors);
    }

    return response;
  };

  const handleSubmitWithResponse = useSubmitWrapper({
    methods,
    handleSubmit: methods.handleSubmit,
    onSubmit,
    isSuccess: (response) => response?.data?.response?.status === ResponseStatus.SUCCESS,
    onSuccess: () => {
      const startDate = methods.getValues("start_date");
      // Only jump to the Active tab for a from-scratch create that starts in the
      // past/now. A duplicate (schedule provided without an id) inherits the
      // original's start date, so it would otherwise land on Active and look
      // like it vanished from the Schedules list.
      const isFreshCreate = !schedule;
      const targetTab =
        isFreshCreate && startDate && startDate.getTime() <= Date.now()
          ? MaintenanceDashboardTabs.ACTIVE
          : MaintenanceDashboardTabs.SCHEDULES;
      navigate(`/maintenance?tab=${targetTab}`);
    },
  });

  const sections: Record<string, TabConfig> = useMemo(() => {
    const derivedSections: Record<string, TabConfig> = {
      ...getStaticMaintenanceScheduleTabConfig(),
    };
    Object.keys(derivedSections).forEach((key) => {
      const tabKey = key as MaintenanceScheduleTabs;
      derivedSections[tabKey] = {
        ...derivedSections[tabKey],
        ...tabState[tabKey],
      };
    });
    return derivedSections;
  }, [tabState]);

  const formTitle = isEditMode ? "Edit Maintenance Schedule" : "Create Maintenance Schedule";
  const submitText = isEditMode ? "Save" : "Create";

  const formErrors: ServerErrors = {
    __all__: [
      ...(errorsNotInForm && !isEmpty(errorsNotInForm) ? ["Something went wrong, please contact support"] : []),
      ...(serverErrorsState?.__all__ || []),
    ],
  };
  return (
    <>
      <FormProvider {...methods}>
        <TabForm
          className={styles.tabContainer}
          tab={tab}
          setTab={setTab}
          title={formTitle}
          sections={sections}
          goBack={goBack}
          serverErrorsState={formErrors}
          formId={"maintenance-schedule-form"}
          fieldToTabMap={MaintenanceScheduleFieldToTabMap}
          prevalidationFields={MaintenanceScheduleFormPrevalidationFields}
          submitText={submitText}
          hasRequiredFields={false}
        >
          <form id={"maintenance-schedule-form"} onSubmit={handleSubmitWithResponse}>
            {Object.entries(sections).map(([key, section]) => (
              <div key={key} style={{display: tab === key ? "block" : "none"}}>
                {section.html()}
              </div>
            ))}
          </form>
        </TabForm>
      </FormProvider>
    </>
  );
};
